import { format, formatDistanceToNow } from 'date-fns'
import {
  getComponentFromManufacturedDate,
  sortComponentsByManufacturedDate,
} from 'features/assetDatabase'
import {
  PartsData,
  PartsTableEntries,
  PartsTableEntry,
  UnitAssetModelTable,
  UnitInspections,
} from 'features/partsDatabase/types/partsTableTypes'
import { ForecastDarkTheme } from 'styles/themes'
import { parseDate } from 'utils/parsingHelpers'

export const getCriticalityTagColor = (criticality: string) => {
  criticality = criticality?.toLowerCase()
  let color
  switch (criticality) {
    case 'low':
      color = ForecastDarkTheme.greenGlassBackground
      break
    case 'medium':
      color = ForecastDarkTheme.blueGlassBackground
      break
    case 'high':
      color = ForecastDarkTheme.redGlassBackground
      break
  }
  return color ?? ForecastDarkTheme.glassBackground
}

export const getAssetModels = (assetData: PartsTableEntries[] | undefined) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'asset-model')
    .map((unit) => {
      return {
        assetType: unit.entry.data.type,
        assetModelUnitTag: unit.entry.tags.unit,
        assetModelData: unit.entry.data,
        assetModelUpdatedAt: unit.entry.updated_at,
      }
    })
}

export const assetInspections = (
  assetData: PartsTableEntries[] | undefined
) => {
  return assetData
    ?.filter((asset) => asset.entry.type === 'inspection')
    .map((inspection) => {
      return {
        inspectionSlug: inspection.entry.id,
        inspectionDate: inspection.entry.data.date,
        inspectionUnitTag: inspection.entry.tags.unit,
        updatedAt: inspection.entry.updated_at,
      }
    })
}

// Finds the latest component for a given unit that isn't after today
// We ignore components after today to enable populating assets in Cantilever before they're manufactured
// If there isn't one before the current date then return the first one in the list
const findCurrentComponent = (unitAssetModel: UnitAssetModelTable) => {
  const components = unitAssetModel?.assetModelData.components ?? {}
  const sortedComponents = sortComponentsByManufacturedDate(
    Object.values(components),
    false
  )
  const currentComponent = getComponentFromManufacturedDate(
    new Date(),
    sortedComponents
  )

  return currentComponent ?? sortedComponents[0]
}

// Finds the latest inspection for a given unit asset model
const findLatestInspection = (
  currentUnitAssetModel: UnitAssetModelTable | undefined,
  unitInspections: UnitInspections[] | undefined
): UnitInspections | undefined => {
  if (
    !currentUnitAssetModel ||
    !unitInspections ||
    unitInspections.length === 0
  ) {
    return undefined
  }

  const unitTag = currentUnitAssetModel.assetModelUnitTag

  const relevantInspections = unitInspections
    .filter((inspection) => inspection.inspectionUnitTag === unitTag)
    .sort((a, b) => {
      const inspectionDateA = parseDate(a.inspectionDate)
      const inspectionDateB = parseDate(b.inspectionDate)

      // Handle potentially invalid dates during sort comparison
      if (!inspectionDateA && !inspectionDateB) return 0
      if (!inspectionDateA) return 1
      if (!inspectionDateB) return -1

      // Sort descending (latest first)
      return inspectionDateB.getTime() - inspectionDateA.getTime()
    })

  return relevantInspections.length > 0 ? relevantInspections[0] : undefined
}

export const convertDateToString = (
  dateInput: Date | undefined
): string | undefined => {
  if (dateInput !== undefined) {
    return format(dateInput, 'EEE MMM dd yyyy')
  } else {
    return undefined
  }
}

export const convertDateToDistanceToNow = (
  dateInput: Date | undefined
): string | undefined => {
  if (dateInput !== undefined) {
    const now = new Date()
    const oneDayInMs = 24 * 60 * 60 * 1000

    // Ensure dateInput is a Date object
    const dateObj = dateInput instanceof Date ? dateInput : parseDate(dateInput)
    if (!dateObj) {
      return undefined
    }

    // Check if the date is less than 1 day ago
    if (now.getTime() - dateObj.getTime() < oneDayInMs) {
      return formatDistanceToNow(dateObj, { addSuffix: true })
    } else {
      return convertDateToString(dateObj)
    }
  } else {
    return undefined
  }
}
const convertBlankToUndefined = (
  stringInput: string | undefined
): string | undefined => {
  if (stringInput?.trim() === '') {
    return undefined
  } else return stringInput?.trim()
}
export const createPartsTableData = (
  unitAssets: PartsTableEntry[],
  unitAssetModels: UnitAssetModelTable[],
  unitInspections: UnitInspections[]
) => {
  const result: PartsData[] = []
  unitAssets.forEach((unitAsset) => {
    const unitAssetModel = unitAssetModels.find(
      (model) => model.assetModelUnitTag === unitAsset.id
    )
    // Unit exists, but no asset model (this should never happen)
    if (!unitAssetModel) {
      return
    }

    const latestInspection = findLatestInspection(
      unitAssetModel,
      unitInspections
    )
    const currentComponent = findCurrentComponent(unitAssetModel)

    result.push({
      name: unitAsset.data.name,
      id: unitAsset.id,
      assetModelType: unitAssetModel?.assetType,
      lastInspectionDate: parseDate(latestInspection?.inspectionDate),
      lastInspectionSlug: latestInspection?.inspectionSlug,
      lastUpdated: parseDate(unitAssetModel?.assetModelUpdatedAt),
      componentType: currentComponent?.type,
      componentName: currentComponent?.name,
      stage: currentComponent?.stage,
      status: unitAssetModel?.assetModelData.status,
      effectiveDate: parseDate(unitAssetModel?.assetModelData.effective_date),
      erpId: convertBlankToUndefined(unitAsset.data.erp_id),
      unitPrimaryImage: unitAsset.data.primary_image,
    })
  })
  result.sort((a, b) => {
    return a.name.localeCompare(b.name)
  })
  return result
}
